import { LocalDate } from "@js-joda/core";

import {
    declareAiCapability,
    provideAiCapability,
} from "@medflyt/webapp-react/src/modules/ai-omnibar/ai-capability-registry";

// Staffing issues describing its own filter bar. Its range is named startDate/endDate rather than
// from/to, which is why the runner writes those params instead.
declareAiCapability({
    id: "staffingIssues.filter",
    title: "Filter the staffing issues list",
    effect: "prefill",
    description:
        "Sets the filters on Visits > Staffing Issues and re-runs its search: the date range, contract, office, and a free-text search over the table. Anything left out keeps what the page already has. Only works while that page is open.",
    location: "the filter bar of the Visits > Staffing Issues page",
    scope: "page",
    permissionKey: "view_visits_section_staffing_issues",
    params: [
        { name: "from", type: "date", required: false, description: "First day to include, as yyyy-MM-dd." },
        { name: "to", type: "date", required: false, description: "Last day to include, as yyyy-MM-dd." },
        {
            name: "contract",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getStaffingIssueContractOptions(),
            description: "Limit to these contracts."
        },
        {
            name: "office",
            type: "enum",
            required: false,
            multiple: true,
            options: () => getStaffingIssueOfficeOptions(),
            description: "Limit to these offices."
        },
        {
            name: "search",
            type: "text",
            required: false,
            searchesFreeText: true,
            description: "Free text the table searches over every column."
        }
    ],
    examples: ["staffing issues this week", "staffing issues for the europe office"]
});

let staffingIssueContracts = () => [];
let staffingIssueOffices = () => [];

function getStaffingIssueContractOptions() {
    return staffingIssueContracts().map((contract) => ({ value: String(contract.id), label: contract.name }));
}

function getStaffingIssueOfficeOptions() {
    return staffingIssueOffices().map((office) => ({ value: String(office.id), label: office.name }));
}

//! @ngInject
export function staffingIssuesCtrl(
  $scope,
  $rootScope,
  patientLookupService,
  NgTableParams,
  itemSearchPageManager,
  FilterUtils,
  toaster,
  dateUtils,
  $filter,
  DatabaseApi,
  visitConsts,
) {

  const initialize = () => {
    $scope.issuesTable = null;
    $scope.globalFilter = { val: "" };
    $scope.issuesCounters = {};
    initAssets();
    if (!$scope.gotVisitSettings) {
      return;
    }
    $scope.firstDayOfWeek = parseInt($rootScope.visitSettings.calendarStartDayOfTheWeek || 0);
    initPageManager();
    $scope.pageManager.resetSearchParamsByUrl();
    initDateRange();
    initDateRangePresets();
    initIssuesTemplates();
    initFilters();
  };

  $scope.singleSelectionExtraSettings = {
    styleActive: true,
    singleSelection: true,
    selectionLimit: 1,
    smartButtonMaxItems: 1,
    closeOnSelect: true,
    showCheckAll: false,
    showUncheckAll: false,
  };

  $scope.dateRangePresetsEvents = {
    onSelectionChanged: () => {
      handleChangeDateRangeIssueSelection($scope.selectedDateRangePreset.id);
    },
  };

  const initPageManager = () => {
    $scope.pageManager = itemSearchPageManager.createSearchPageManager("/staffing_issues");
    $scope.validatorsDataManager = $scope.pageManager.getValidatorsDataManager("STAFFING_ISSUES");
    $scope.contractTypesDataManager = $scope.pageManager.getContractTypesDataManager();
    $scope.officeDataManager = $scope.pageManager.getOfficeDataManager();
    $scope.pageManager.initContractTypesParam();
    $scope.pageManager.initOfficeParam();
    $scope.pageManager.initFromToDateParams(false, false, "startDate", "endDate");
    $scope.pageManager.initValidatorsParam("issues");
  };

  const initDateRange = () => {
    $scope.datePickerOptions = {
      startDate: {
        startingDay: $scope.firstDayOfWeek,
        dateDisabled: ({date}) => {
          return date.getDay() !== $scope.firstDayOfWeek;
        }
      },
      endDate: {
        startingDay: $rootScope.visitSettings.calendarStartDayOfTheWeek,
        dateDisabled: ({date}) => {
          let lastDayOfWeek = $scope.firstDayOfWeek + 6;
          if (lastDayOfWeek > 6) {
            lastDayOfWeek -= 7;
          }
          return date.getDay() !== lastDayOfWeek;
        }
      }
    };

    $scope.datesChangeHandlers = {
      startDate: () => {
        const newStartDate = $scope.pageManager.searchParams.startDate.value;
        if (!newStartDate || newStartDate.getDay() !== $scope.firstDayOfWeek) {
          $scope.pageManager.updateSearchParamValue("startDate", "");
          return;
        }
        const oldEndDate = $scope.pageManager.searchParams.endDate.value;
        const localDateNewStartDate = dateUtils.dateToLocalDate(newStartDate);
        const localDateOldEndDate = oldEndDate === "" ? "" : dateUtils.dateToLocalDate(oldEndDate)
        if (oldEndDate === "" || localDateOldEndDate.isBefore(localDateNewStartDate)) {
          const newEndDate = dateUtils.localDateToDate(localDateNewStartDate.plusDays(6));
          $scope.pageManager.updateSearchParamValue("endDate", newEndDate);
        }
        $scope.datePickerOptions.endDate.minDate = newStartDate;
        const localDateNewEndDate = dateUtils.dateToLocalDate($scope.pageManager.searchParams.endDate.value);
        setPresetByLocalDates(localDateNewStartDate, localDateNewEndDate);
      },
      endDate: () => {
        const newEndDate = $scope.pageManager.searchParams.endDate.value;
        if (!newEndDate || newEndDate.getDay() !== $scope.firstDayOfWeek - 1 && newEndDate.getDay() !== $scope.firstDayOfWeek + 6) {
          $scope.pageManager.updateSearchParamValue("endDate", "");
          return;
        }
        const oldStartDate = $scope.pageManager.searchParams.startDate.value;
        const localDateNewEndDate = dateUtils.dateToLocalDate(newEndDate);
        const localDateOldStartDate = oldStartDate === "" ? "" : dateUtils.dateToLocalDate(oldStartDate);
        if (oldStartDate === "" || localDateOldStartDate.isAfter(localDateNewEndDate)) {
          const newStartDate = dateUtils.localDateToDate(localDateNewEndDate.minusDays(6));
          $scope.pageManager.updateSearchParamValue("startDate", newStartDate);
        }
        $scope.datePickerOptions.startDate.maxDate = newEndDate;
        const localDateNewStartDate = dateUtils.dateToLocalDate($scope.pageManager.searchParams.startDate.value);
        setPresetByLocalDates(localDateNewStartDate, localDateNewEndDate);
      }
    };
  };

  const setPresetByLocalDates = (startDate, endDate) => {
    const matchingPreset = $scope.dateRangePresetsOptions.find(preset =>
      preset.startDate.equals(startDate) &&
      preset.endDate.equals(endDate)
    );
    $scope.selectedDateRangePreset = {};
    if (matchingPreset) {
      $scope.selectedDateRangePreset.id = matchingPreset.id;
    }
  };

  const getStartOfWeek = () => {
    const nowLocalDate = LocalDate.now();
    let nowDayOfWeek = nowLocalDate.dayOfWeek().value(); // 1 [MONDAY] - 7 [SUNDAY]
    if (nowDayOfWeek === 7) nowDayOfWeek = 0; // SUNDAY
    let sub = nowDayOfWeek - $scope.firstDayOfWeek; // firstDayOfWeek (0 [SUNDAY] - 6 [SATURDAY])
    if (sub < 0) sub += 7;
    const startOfWeekLocalDate = nowLocalDate.minusDays(sub);
    return startOfWeekLocalDate;
  };

  const initDateRangePresets = () => {
    const startOfThisWeek = getStartOfWeek();
    $scope.dateRangePresetsOptions = [
      { id: 1, label: "Last 4 weeks", startDate: startOfThisWeek.minusWeeks(4), endDate: startOfThisWeek.minusDays(1) },
      { id: 2, label: "Last 2 weeks", startDate: startOfThisWeek.minusWeeks(2), endDate: startOfThisWeek.minusDays(1) },
      { id: 3, label: "Last Week", startDate: startOfThisWeek.minusWeeks(1), endDate: startOfThisWeek.minusDays(1) },
      { id: 4, label: "This Week", startDate: startOfThisWeek, endDate: startOfThisWeek.plusWeeks(1).minusDays(1) },
      { id: 5, label: "Next Week", startDate: startOfThisWeek.plusWeeks(1), endDate: startOfThisWeek.plusWeeks(2).minusDays(1) },
      { id: 6, label: "Next 2 weeks", startDate: startOfThisWeek.plusWeeks(1), endDate: startOfThisWeek.plusWeeks(3).minusDays(1) },
      { id: 7, label: "Next 4 weeks", startDate: startOfThisWeek.plusWeeks(1), endDate: startOfThisWeek.plusWeeks(5).minusDays(1) },
    ];
    $scope.selectedDateRangePreset = {id : 4};
    handleChangeDateRangeIssueSelection(4);
  };

  const handleChangeDateRangeIssueSelection = (dateRangePresetId) => {
    const dateRangePreset = $scope.dateRangePresetsOptions.find(preset =>
      preset.id === dateRangePresetId
    );
    if (dateRangePreset) {
      $scope.pageManager.updateSearchParamValue("startDate", dateUtils.localDateToDate(dateRangePreset.startDate));
      $scope.pageManager.updateSearchParamValue("endDate", dateUtils.localDateToDate(dateRangePreset.endDate));
    }
  };

  const initIssuesTemplates = () => {
    $scope.issuesTemplates = [
      {
        id: 1,
        label: "Patients",
        issues: visitConsts.staffingIssuesValidatorIds
          .filter(issue => issue.template === "PATIENTS")
          .map(issue => issue.id)
      },
      {
        id: 2,
        label: "Caregivers",
        issues: visitConsts.staffingIssuesValidatorIds
          .filter(issue => issue.template === "CAREGIVERS")
          .map(issue => issue.id)
      }
    ];
    $scope.selectedIssuesTemplate = { id: 1 };
    $scope.issuesTemplatesEvents = {
      onSelectionChanged: () => {
        handleChangeIssuesTemplateSelection($scope.selectedIssuesTemplate.id);
      },
    };
  };

  const handleChangeIssuesTemplateSelection = (issuesTemplateId) => {
    const templateSettings = $scope.issuesTemplates.find(template =>
      template.id === issuesTemplateId
    );
    if (templateSettings !== undefined) {
      initValidatorsFromTemplate(templateSettings);
    }
  };

  const initFilters = () => {
    const storageFilters = FilterUtils.rootFilters.staffingIssuesCtrl;
    if (storageFilters !== undefined) {
      $scope.pageManager.setSearchParams(storageFilters);
      initValidatorsFilterFromStorage(storageFilters);
      setPresetByLocalDates(
        dateUtils.dateToLocalDate(storageFilters.startDate.value),
        dateUtils.dateToLocalDate(storageFilters.endDate.value)
      );
    } else if ($scope.selectedIssuesTemplate.id) {
      handleChangeIssuesTemplateSelection($scope.selectedIssuesTemplate.id);
    }
  };

  const initValidatorsFromTemplate = (issuesTemplate) => {
    const validators = $scope.validatorsDataManager.getAllValidators();
    validators.forEach((validator) => {
      const shouldBeSelected = issuesTemplate.issues.includes(validator.id);
      if (validator.isSelected !== shouldBeSelected) {
        $scope.validatorsDataManager.toggleSelection(validator);
      }
    });
    $scope.pageManager.updateSearchParamValue("issues", validators);
  };

  const initValidatorsFilterFromStorage = (storageFilters) => {
    const validatorsToDeselectMap = {};
    storageFilters.issues.value.forEach(validator => {
      if (validator.isSelected === false) {
        validatorsToDeselectMap[validator.id] = true;
      }
    });
    const validators = $scope.validatorsDataManager.getAllValidators();
    validators.forEach((validator) => {
      if (validator.isSelected && validatorsToDeselectMap[validator.id]) {
        $scope.validatorsDataManager.toggleSelection(validator);
      }
    });
    $scope.pageManager.updateSearchParamValue("issues", validators);
  };

  $scope.areAllValidatorsSelected = () => {
    if (!$scope.validatorsDataManager) {
      return false;
    }
    const allValidators = $scope.validatorsDataManager.getAllValidators();
    const selectedValidators = allValidators.filter(v => v.isSelected);
    return selectedValidators.length === allValidators.length;
  };

  $scope.toggleSelectAllValidators = () => {
    const selection = !$scope.areAllValidatorsSelected();
    $scope.validatorsDataManager.getAllValidators().forEach((validator) => {
      if (validator.isSelected !== selection) {
        $scope.validatorsDataManager.toggleSelection(validator)
      }
    });
  };


  staffingIssueContracts = () => $scope.contractTypesDataManager?.getActiveContractTypes() ?? [];
  staffingIssueOffices = () => $scope.officeDataManager?.getOffices() ?? [];

  const releaseStaffingIssuesFilter = provideAiCapability("staffingIssues.filter", (args) => {
        const applied = [];
        const asList = (value) => (Array.isArray(value) ? value : [value]);
        const asDate = (text) => {
            const parts = String(text).split("-").map(Number);

            if (parts.length !== 3 || parts.some(isNaN)) {
                throw new Error("Could not read the date " + text + ".");
            }

            return new Date(parts[0], parts[1] - 1, parts[2]);
        };
        const pickAll = (available, requested, what) =>
            asList(requested).map((one) => {
                const found = (available ?? []).find((item) => String(item.id) === String(one));

                if (found === undefined) {
                    throw new Error("This agency has no " + what + " " + one + ".");
                }

                return found;
            });

        if (args.from !== undefined) {
            $scope.pageManager.searchParams.startDate.value = asDate(args.from);
        }

        if (args.to !== undefined) {
            $scope.pageManager.searchParams.endDate.value = asDate(args.to);
        }

        if (args.from !== undefined || args.to !== undefined) {
            applied.push([args.from, args.to].filter(Boolean).join(" to "));
        }

        if (args.contract !== undefined) {
            const contracts = pickAll(staffingIssueContracts(), args.contract, "contract");

            $scope.pageManager.searchParams.selectedContractTypes.value = contracts;
            applied.push(contracts.map((contract) => contract.name).join(" and "));
        }

        if (args.office !== undefined) {
            const offices = pickAll(staffingIssueOffices(), args.office, "office");

            $scope.pageManager.searchParams.selectedOffices.value = offices;
            applied.push(offices.map((office) => office.name).join(" and "));
        }

        if (args.search !== undefined) {
            $scope.globalFilter.val = String(args.search);
            applied.push('"' + String(args.search) + '"');
        }

        $scope.searchStaffingIssues();

        return applied.length === 0
            ? "Re-ran the staffing issues search"
            : "Showing staffing issues: " + applied.join(", ");
  });

  $scope.$on("$destroy", releaseStaffingIssuesFilter);

  $scope.searchStaffingIssues = () => {
    if (!$scope.gotAssets) {
      return;
    }
    $scope.globalFilter.val = "";
    $scope.pageManager.executeSearch().then((res) => {
      mapItemsAsync(res.data.issues).then((mapped) => {
        $scope.staffingIssues = mapped;
        initTable();
      });
    }, (error) => {
      console.error(error);
      toaster.pop("error", "Failed to load staffing issues");
      $scope.staffingIssues = [];
      $scope.issuesCounters = {};
      initTable();
    });
  };

  // The names for the patients on this page only, filled in by mapItemsAsync.
  let patientsById = new Map();

  // Resolves the ids on the page, then maps. The dictionary of every patient in
  // the agency used to stand in for this.
  const mapItemsAsync = (items) =>
    patientLookupService
      .byIds(items.map(item => item.patientId))
      .then(resolved => {
        patientsById = resolved;
        return mapItems(items);
      });

  const mapItems = (items) => {
    $scope.issuesCounters = Object.fromEntries(
      visitConsts.staffingIssuesValidatorIds.map(issue => [issue.id, 0])
    );
    return items.map(item => {
      if (item.patientId !== undefined) {
        item.patientDisplayId = item.patientDisplayId === -1 ? "" : item.patientDisplayId;
        const patient = patientsById.get(String(item.patientId));
        if (patient) {
          item.patientFirstLastName = `${patient.firstName} ${patient.lastName}`;
          if (patient.middleName && patient.middleName !== "") {
            item.patientFirstMiddleLastName = `${patient.firstName} ${patient.middleName} ${patient.lastName}`;
            item.patientLastMiddleFirstName = `${patient.lastName} ${patient.middleName} ${patient.firstName}`;
          }
        }
      }
      if (item.caregiverId !== undefined) {
        item.caregiverDisplayId = item.caregiverDisplayId === -1 ? "" : item.caregiverDisplayId;
        // These are never rendered - they exist so the table's global filter,
        // which deep-matches every row property, finds a row by the caregiver's
        // name in any order. The name parts now arrive on the row.
        if (item.caregiverFirstName && item.caregiverLastName) {
          item.caregiverFirstLastName = `${item.caregiverFirstName} ${item.caregiverLastName}`;
          if (item.caregiverMiddleName && item.caregiverMiddleName !== "") {
            item.caregiverFirstMiddleLastName = `${item.caregiverFirstName} ${item.caregiverMiddleName} ${item.caregiverLastName}`;
            item.caregiverLastMiddleFirstName = `${item.caregiverLastName} ${item.caregiverMiddleName} ${item.caregiverFirstName}`;
          }
        }
      }
      if ($scope.issuesCounters[item.issue] !== undefined) {
        $scope.issuesCounters[item.issue]++;
      }
      return item;
    });
  };

  const initTable = () => {
    const options = {
      count: 25
    };
    $scope.issuesTable = new NgTableParams(options, {
      dataset: $scope.staffingIssues
    });
  };

  $scope.applyGlobalSearch = (term) => {
    const filter = { $: term };
    if ($scope.issuesTable) {
      angular.extend($scope.issuesTable.filter(), filter);
    }
  };

  $scope.exportTable = () => {
    const tableData = $filter('filter')($scope.staffingIssues, $scope.globalFilter.val);
    if (tableData.length <= 0) {
      toaster.pop('warning', 'Nothing to export', 'The table is empty');
      return;
    }

    $scope.loadingCSV = true;

    const rows = [];
    const titles = [
      "Issue",
      "Patient Name",
      "Patient ID",
      "Caregiver Name",
      "Caregiver ID",
      "Visit ID",
      "Visit Date"
    ];

    rows.push(titles);

    tableData.forEach(dataRow => {
      const csvRow = [];

      titles.forEach((title) => {
        if (title === "Issue") csvRow.push(`"${dataRow.issueDisplayText || ""}"`);
        else if (title === "Patient Name") csvRow.push(`"${(dataRow.patientName || "").replace(/(\r\n|\n|\r)/gm, "")}"`);
        else if (title === "Patient ID") csvRow.push(`"${dataRow.patientDisplayId || ""}"`);
        else if (title === "Caregiver Name") csvRow.push(`"${(dataRow.caregvierName || "").replace(/(\r\n|\n|\r)/gm, "")}"`);
        else if (title === "Caregiver ID") csvRow.push(`"${dataRow.caregiverDisplayId || ""}"`);
        else if (title === "Visit ID") csvRow.push(`"${dataRow.visitId || ""}"`);
        else if (title === "Visit Date") csvRow.push(`"${$filter("mfShortDate")(dataRow.visitDate)}"`);
        else csvRow.push("");
      });

      rows.push(csvRow);
    });

    let csvContent = "";
    rows.forEach((rowArray) => {
      const row = rowArray.join(",");
      csvContent += row + "\r\n";
    });

    const universalBOM = "\uFEFF";
    const encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(universalBOM + csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `medflyt-export-staffing-issues-${$filter("date")(new Date(), "yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);

    link.click();
    $scope.loadingCSV = false;
  };

  const initAssets = () => {
    $scope.initializeMap = DatabaseApi.entitiesInitializeMap();
    checkAssets();
  };

  const checkAssets = () => {
    // Patients are no longer an asset to wait for - they are resolved per page.
    $scope.gotVisitSettings = $scope.initializeMap['visitSettings'];
    $scope.gotAssets = true;
  };
  
  $scope.getIssueCounterSuffix = (issue) => {
    if (!$scope.issuesCounters[issue]) {
      return "";
    }
    return ` (${$scope.issuesCounters[issue]})`;
  };

  $rootScope.$on("got_visit_settings", () => {
    initialize();
  });

  $scope.$on("$destroy", () => {
    const filters = $scope.pageManager.searchParams;
    FilterUtils.rootFilters.staffingIssuesCtrl = angular.copy(filters);
  });

  initialize();
};